import { detectPlatform } from "../platform.js";

/**
 * Native bridge to Android's `View.setSystemGestureExclusionRects()`.
 *
 * While a calendar block is being long-press-dragged on touch, the finger is
 * driven to the very screen edge to page the day window (`edgePageZonePx`).
 * That edge region is also where Samsung's Edge Panel / "open in split window"
 * gesture lives, so the OS would otherwise hijack the drag. Toggling exclusion
 * on for the duration of the drag tells the system to let the WebView keep
 * those edge touches.
 *
 * The native plugin (EdgeGesturePlugin) computes the rects itself (a strip down
 * each edge); JS only flips it on/off. No-op off Android / below API 29.
 * Fire-and-forget — failures (older OS, missing plugin) are swallowed.
 */
interface EdgeGestureBridge {
  setExclusion(options: { enabled: boolean }): Promise<void>;
}

let bridge: EdgeGestureBridge | null = null;
let unsupported = false;

function get(): EdgeGestureBridge | null {
  if (unsupported || detectPlatform() !== "capacitor") return null;
  if (!bridge) {
    try {
      // Capacitor injects window.Capacitor.registerPlugin at runtime; it returns
      // the native proxy synchronously (no async import needed).
      const core = (window as unknown as { Capacitor?: { registerPlugin?: (n: string) => EdgeGestureBridge } }).Capacitor;
      if (!core?.registerPlugin) { unsupported = true; return null; }
      bridge = core.registerPlugin("EdgeGesture");
    } catch {
      unsupported = true;
      return null;
    }
  }
  return bridge;
}

/**
 * Claim (on) / release (off) the screen's left+right edges as gesture-exclusion
 * zones. Call `true` when a touch block-drag arms, `false` when it ends/cancels.
 */
export function setEdgeGestureExclusion(on: boolean): void {
  const b = get();
  if (!b) return;
  void b.setExclusion({ enabled: on }).catch(() => { /* unsupported device/OS */ });
}
